import React, { memo, useCallback } from 'react'
import { styled } from '@mui/system'
import { colorPalette } from '../../style/colorPalette'

type RadioButtonSize = 's' | 'm'

type Props = {
  /** Whether the radio button is checked */
  checked: boolean
  /** Callback when the radio button is changed */
  onChange: (checked: boolean) => void
  /** Size variant of the radio button */
  size?: RadioButtonSize
  /** Whether the radio button is disabled */
  disabled?: boolean
  /** Name attribute for the radio input */
  name?: string
}

const SIZE_CONFIG = {
  s: {
    outer: 18,
    inner: 8,
  },
  m: {
    outer: 20,
    inner: 10,
  },
} as const

const Container = styled('label')<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
}))

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
})

const RadioCircle = styled('span')<{
  checked: boolean
  size: RadioButtonSize
}>(({ checked, size }) => {
  const config = SIZE_CONFIG[size]
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: config.outer,
    height: config.outer,
    borderRadius: '50%',
    border: `1px solid ${checked ? colorPalette.skyBlue[7] : colorPalette.monotone[2]}`,
    backgroundColor: colorPalette.monotone[0],
    transition: 'border-color 0.15s ease-in-out',
    '&::after': {
      content: '""',
      display: checked ? 'block' : 'none',
      width: config.inner,
      height: config.inner,
      borderRadius: '50%',
      backgroundColor: colorPalette.skyBlue[7],
    },
  }
})

export const RadioButton: React.FC<Props> = memo(
  ({ checked, onChange, size = 's', disabled = false, name }) => {
    const onInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked)
      },
      [onChange]
    )

    return (
      <Container disabled={disabled}>
        <HiddenInput
          type="radio"
          checked={checked}
          disabled={disabled}
          name={name}
          onChange={onInputChange}
        />
        <RadioCircle checked={checked} size={size} />
      </Container>
    )
  }
)

RadioButton.displayName = 'RadioButton'

export default RadioButton
